import React from 'react';
import { Wrench, Package, Settings, Flame, ArrowRight, Phone, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const MaintenanceSAV = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <SEO 
        title="Maintenance & Services Industriels | ORION INDUSTRIE"
        description="Services de maintenance industrielle ORION INDUSTRIE : maintenance des équipements de quai, maintenance industrielle et soudage sur site. Intervention nationale depuis Nevers (Nièvre)."
        keywords="maintenance industrielle, maintenance quai chargement, soudage industriel, SAV équipements quai, maintenance préventive, dépannage industriel"
      />
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#020d2a] to-slate-800 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Wrench className="w-12 h-12 text-[#FF6600]" />
            <h1 className="text-4xl sm:text-5xl font-bold">
              Maintenance & Services Industriels
            </h1>
          </div>
          <p className="text-2xl text-orange-400 font-semibold mb-4">
            Trois expertises au service de votre activité
          </p>
          <p className="text-xl text-slate-300 max-w-4xl mb-8">
            ORION INDUSTRIE vous accompagne dans l'entretien, la réparation et la remise en état 
            de vos équipements de quai et installations industrielles.
          </p>
          <a href="tel:+33659452562">
            <Button 
              size="lg"
              className="bg-[#FF6600] hover:bg-[#E65A00] text-white"
            >
              <Phone className="mr-2 w-5 h-5" />
              Dépannage : 06 59 45 25 62
            </Button>
          </a>
        </div>
      </section>

      {/* 3 Pôles */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Pôle 1 - Maintenance Quai */}
            <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl p-8 shadow-xl border-2 border-[#FF6600] hover:shadow-2xl transition-shadow">
              <div className="w-16 h-16 bg-[#FF6600] rounded-xl flex items-center justify-center mb-6">
                <Package className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Maintenance des Équipements de Quai
              </h2>
              <p className="text-slate-600 mb-6">
                Entretien, révision et réparation de niveleurs de quai, portes sectionnelles, 
                rideaux métalliques et équipements périphériques.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  'Révision de niveleurs',
                  'Maintenance portes industrielles',
                  'Pack Maintenance Quai (porte + niveleur)',
                  'Contrats d\'entretien',
                  'Dépannage en 24-48h',
                  'Pièces détachées'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-[#FF6600] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-lg p-4 mb-6 border border-[#FF6600]">
                <p className="text-sm text-slate-600">
                  <strong className="text-slate-900">Offre prioritaire :</strong> Pack Maintenance Quai - 
                  un seul interlocuteur pour l'entretien de vos portes et niveleurs
                </p>
              </div>

              <Link to="/maintenance-quai">
                <Button 
                  className="w-full bg-[#FF6600] hover:bg-[#E65A00] text-white"
                  size="lg"
                >
                  Découvrir nos offres
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            {/* Pôle 2 - Maintenance Industrielle */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border-2 border-slate-200">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Settings className="w-10 h-10 text-blue-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Maintenance Industrielle
              </h2>
              <p className="text-slate-600 mb-6">
                Maintenance préventive et curative, réparation d'équipements et remise en état 
                d'installations industrielles.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  'Maintenance préventive',
                  'Maintenance curative',
                  'Maintenance mécanique industrielle',
                  'Réparation d\'équipements',
                  'Intervention sur machines',
                  'Remise en état d\'installations'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-slate-600">
                  <strong className="text-slate-900">Intervention sur site :</strong> Diagnostic, réparation 
                  et remise en service de vos équipements industriels
                </p>
              </div>

              <Link to="/maintenance-industrielle">
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                  size="lg"
                >
                  En savoir plus
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            {/* Pôle 3 - Soudage */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow border-2 border-slate-200">
              <div className="w-16 h-16 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <Flame className="w-10 h-10 text-purple-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">
                Soudage & Réparation Métallique
              </h2>
              <p className="text-slate-600 mb-6">
                Travaux de soudage et réparation métallique directement sur vos installations. 
                Intervention mobile avec poste autonome.
              </p>

              <div className="space-y-3 mb-8">
                {[
                  'Soudage industriel sur site',
                  'Réparation structures métalliques',
                  'Réparation équipements',
                  'Intervention avec poste autonome',
                  'Sans démontage nécessaire',
                  'Remise en état éléments métalliques'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="bg-purple-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-slate-600">
                  <strong className="text-slate-900">Mobilité :</strong> Intervention directe sur votre site 
                  avec notre poste de soudage autonome
                </p>
              </div>

              <Link to="/soudage-reparation-metallique">
                <Button 
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                  size="lg"
                >
                  En savoir plus
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Intervention Nationale
            </h2>
            <p className="text-xl text-slate-700 mb-6">
              <strong>ORION INDUSTRIE</strong> est basée à <strong>Nevers, dans la Nièvre (58)</strong>.
            </p>
            <p className="text-lg text-slate-600">
              Nous intervenons sur des sites industriels et logistiques partout en France selon la nature 
              des travaux et les conditions d'intervention. Les déplacements sont adaptés à chaque projet 
              et intégrés à nos devis.
            </p>
          </div>
        </div>
      </section>

      {/* Dépannage urgent */}
      <section className="py-20 bg-gradient-to-r from-[#020d2a] to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Besoin d'un Dépannage Urgent ?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Intervention selon localisation • Dépannage en 24-48h
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+33659452562">
              <Button 
                size="lg"
                className="bg-[#FF6600] hover:bg-[#E65A00] text-white"
              >
                <Phone className="mr-2 w-5 h-5" />
                06 59 45 25 62
              </Button>
            </a>
            <Button 
              onClick={() => window.location.href = '/#quote'}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10"
            >
              Demander un devis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
          <p className="text-sm text-slate-400 mt-6">
            Compatible toutes marques selon disponibilité des stocks fabricants
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MaintenanceSAV;
